import { Locale } from 'date-fns'
import * as dateFnsLocales from 'date-fns/locale'

/**
 * Formats a date into a string based on the provided locale.
 *
 * @param date
 * @param locale
 * @param includeTime
 * @return Formatted date string
 */
export const formatDateTime = (date: Date | string, locale: string, includeTime = true) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }

  try {
    const datePart = dateObj.toLocaleDateString(locale, dateOptions)
    const timePart = includeTime ? dateObj.toLocaleTimeString(locale, timeOptions) : ''

    return includeTime ? `${datePart} ${timePart}` : datePart
  } catch (error) {
    console.warn(`Locale ${locale} not supported, using ISO format`, error)
    return includeTime ? dateObj.toISOString().replace('T', ' ').slice(0, 19) : dateObj.toISOString().slice(0, 10)
  }
}

/**
 * Formats a date into a string based on the provided locale without time.
 *
 * @param date
 * @param locale
 * @return Formatted date string without time
 */
export const formatDate = (date: Date | string, locale: string) => formatDateTime(date, locale, false)

/**
 * Formats a date into a long format with a full month name.
 * Example: "15 June 2024" or "15 Haziran 2024"
 *
 * @param date - Date string or Date object
 * @param locale - Locale string (e.g., 'en-US', 'tr-TR')
 * @return Formatted date string with full month name
 */
export const formatLongDate = (date: Date | string, locale: string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }

  try {
    return dateObj.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  } catch (error) {
    console.warn(`Locale ${locale} not supported, using default format`, error)
    return dateObj.toISOString().slice(0, 10)
  }
}

/**
 * Formats a date into a long format with a full month name and time.
 * Example: "15 June 2024, 16:20" or "15 Haziran 2024, 16:20"
 *
 * @param date - Date string or Date object
 * @param locale - Locale string (e.g., 'en-US', 'tr-TR')
 * @return Formatted date and time string with full month name
 */
export const formatLongDateTime = (date: Date | string | null | undefined, locale: string): string => {
  if (!date) {
    return ''
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }

  try {
    return dateObj.toLocaleString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.warn(`Locale ${locale} not supported, using default format`, error)
    return dateObj.toISOString().replace('T', ' ').slice(0, 16)
  }
}

/**
 * Formats a Date object into a local input-friendly string: YYYY-MM-DDTHH:mm
 * This is suitable for storing local datetime without seconds/timezone.
 */
export const formatToLocalInput = (d: Date | undefined | null): string => {
  if (!d || isNaN(d.getTime())) {
    return ''
  }
  const pad = (n: number) => n.toString().padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

/**
 * Parses a local input string (YYYY-MM-DDTHH:mm) to Date.
 * Returns undefined for empty/invalid values.
 */
export const parseLocalInput = (value?: string | null): Date | undefined => {
  if (!value) {
    return undefined
  }
  const d = new Date(value)
  return isNaN(d.getTime()) ? undefined : d
}

/**
 * Resolves a locale string to a date-fns Locale object.
 * It tries the full locale (e.g., 'tr-TR'), then the language part (e.g., 'tr'), and falls back to enUS.
 *
 * @param locale - The locale string to resolve (e.g., 'en-US', 'tr-TR')
 * @return The corresponding date-fns Locale object
 */
export const resolveLocale = (locale: string): Locale => {
  // 'tr-TR' → try 'trTR', then 'tr', then fall back to enUS
  const camelCase = locale.replace(/-([a-z])/gi, (_, c: string) => c.toUpperCase())
  const lang = locale.split('-')[0]

  return (
    (dateFnsLocales as Record<string, Locale>)[camelCase] ??
    (dateFnsLocales as Record<string, Locale>)[lang] ??
    dateFnsLocales.enUS
  )
}
